import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Palette, Type, Image, Layout, Save, RefreshCw, Loader2, Plus, Trash2, Eye
} from 'lucide-react';
import { designAPI } from '../api';

const FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 
  'Playfair Display', 'Merriweather', 'Source Sans Pro', 'Raleway'
];

const SHADOW_STYLES = [
  { value: 'none', label: 'None' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'medium', label: 'Medium' },
  { value: 'strong', label: 'Strong' }
];

export const DesignManagerPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [design, setDesign] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchDesignSystem();
  }, []);

  const fetchDesignSystem = async () => {
    try {
      setLoading(true);
      const res = await designAPI.get();
      setDesign(res.data);
    } catch (error) {
      console.error('Failed to fetch design system:', error);
      toast.error('Failed to load design system');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await designAPI.update(design);
      toast.success('Design system saved');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save design system');
    } finally {
      setSaving(false);
    }
  };

  const updateDesign = (field, value) => {
    setDesign({ ...design, [field]: value });
    setHasChanges(true);
  };

  const addCustomColor = () => {
    const customColors = design.custom_colors || [];
    customColors.push({ name: `Color ${customColors.length + 1}`, hex: '#6B7280' });
    updateDesign('custom_colors', customColors);
  };

  const updateCustomColor = (index, field, value) => {
    const customColors = [...(design.custom_colors || [])];
    customColors[index] = { ...customColors[index], [field]: value };
    updateDesign('custom_colors', customColors);
  };

  const removeCustomColor = (index) => {
    const customColors = (design.custom_colors || []).filter((_, i) => i !== index);
    updateDesign('custom_colors', customColors);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="design-manager-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Palette className="h-6 w-6" />
            Design Manager
          </h1>
          <p className="text-muted-foreground">
            Configure your brand design system for consistent marketing materials
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchDesignSystem}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Button onClick={handleSave} disabled={saving || !hasChanges}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="colors">
        <TabsList>
          <TabsTrigger value="colors">
            <Palette className="h-4 w-4 mr-2" />
            Colors
          </TabsTrigger>
          <TabsTrigger value="typography">
            <Type className="h-4 w-4 mr-2" />
            Typography
          </TabsTrigger>
          <TabsTrigger value="logos">
            <Image className="h-4 w-4 mr-2" />
            Logos & Assets
          </TabsTrigger>
          <TabsTrigger value="layout">
            <Layout className="h-4 w-4 mr-2" />
            Layout
          </TabsTrigger>
        </TabsList>

        {/* Colors Tab */}
        <TabsContent value="colors" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Brand Colors</CardTitle>
              <CardDescription>Define your primary brand color palette</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {/* Primary Color */}
                <div className="space-y-2">
                  <Label>Primary</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={design?.primary_color || '#3B82F6'}
                      onChange={(e) => updateDesign('primary_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={design?.primary_color || '#3B82F6'}
                      onChange={(e) => updateDesign('primary_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Secondary Color */}
                <div className="space-y-2">
                  <Label>Secondary</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={design?.secondary_color || '#10B981'}
                      onChange={(e) => updateDesign('secondary_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={design?.secondary_color || '#10B981'}
                      onChange={(e) => updateDesign('secondary_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Accent Color */}
                <div className="space-y-2">
                  <Label>Accent</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={design?.accent_color || '#F59E0B'}
                      onChange={(e) => updateDesign('accent_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={design?.accent_color || '#F59E0B'}
                      onChange={(e) => updateDesign('accent_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Background Color */}
                <div className="space-y-2">
                  <Label>Background</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={design?.background_color || '#FFFFFF'}
                      onChange={(e) => updateDesign('background_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={design?.background_color || '#FFFFFF'}
                      onChange={(e) => updateDesign('background_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Text Color */}
                <div className="space-y-2">
                  <Label>Text</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={design?.text_color || '#1F2937'}
                      onChange={(e) => updateDesign('text_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={design?.text_color || '#1F2937'}
                      onChange={(e) => updateDesign('text_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Muted Color */}
                <div className="space-y-2">
                  <Label>Muted</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={design?.muted_color || '#6B7280'}
                      onChange={(e) => updateDesign('muted_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={design?.muted_color || '#6B7280'}
                      onChange={(e) => updateDesign('muted_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Success Color */}
                <div className="space-y-2">
                  <Label>Success</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={design?.success_color || '#22C55E'}
                      onChange={(e) => updateDesign('success_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={design?.success_color || '#22C55E'}
                      onChange={(e) => updateDesign('success_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Error Color */}
                <div className="space-y-2">
                  <Label>Error</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={design?.error_color || '#EF4444'}
                      onChange={(e) => updateDesign('error_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={design?.error_color || '#EF4444'}
                      onChange={(e) => updateDesign('error_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Custom Colors */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-base">Custom Colors</Label>
                  <Button variant="outline" size="sm" onClick={addCustomColor}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Color
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(design?.custom_colors || []).map((color, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          value={color.name}
                          onChange={(e) => updateCustomColor(idx, 'name', e.target.value)}
                          placeholder="Color name"
                          className="flex-1"
                        />
                        <Button variant="ghost" size="icon" onClick={() => removeCustomColor(idx)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="color"
                          value={color.hex}
                          onChange={(e) => updateCustomColor(idx, 'hex', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={color.hex}
                          onChange={(e) => updateCustomColor(idx, 'hex', e.target.value)}
                          className="flex-1 font-mono text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Color Preview */}
              <div className="border-t pt-6">
                <Label className="text-base mb-4 block">Preview</Label>
                <div 
                  className="p-6 rounded-lg"
                  style={{ backgroundColor: design?.background_color || '#FFFFFF' }}
                >
                  <h2 
                    className="text-2xl font-bold mb-2"
                    style={{ color: design?.text_color || '#1F2937' }}
                  >
                    Sample Heading
                  </h2>
                  <p 
                    className="mb-4"
                    style={{ color: design?.muted_color || '#6B7280' }}
                  >
                    This is sample body text showing your typography and color choices.
                  </p>
                  <div className="flex gap-2">
                    <button 
                      className="px-4 py-2 rounded-lg text-white"
                      style={{ backgroundColor: design?.primary_color || '#3B82F6' }}
                    >
                      Primary Button
                    </button>
                    <button 
                      className="px-4 py-2 rounded-lg text-white"
                      style={{ backgroundColor: design?.secondary_color || '#10B981' }}
                    >
                      Secondary
                    </button>
                    <button 
                      className="px-4 py-2 rounded-lg text-white"
                      style={{ backgroundColor: design?.accent_color || '#F59E0B' }}
                    >
                      Accent
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Typography Tab */}
        <TabsContent value="typography" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Typography</CardTitle>
              <CardDescription>Configure fonts and text styles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Heading Font</Label>
                  <Select 
                    value={design?.heading_font || 'Inter'} 
                    onValueChange={(v) => updateDesign('heading_font', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONTS.map(font => (
                        <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                          {font}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Body Font</Label>
                  <Select 
                    value={design?.body_font || 'Inter'} 
                    onValueChange={(v) => updateDesign('body_font', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONTS.map(font => (
                        <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                          {font}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Accent Font (Optional)</Label>
                  <Select 
                    value={design?.accent_font || 'none'} 
                    onValueChange={(v) => updateDesign('accent_font', v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {FONTS.map(font => (
                        <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                          {font}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Typography Preview */}
              <div className="border-t pt-6">
                <Label className="text-base mb-4 block">Typography Preview</Label>
                <div className="space-y-4 p-6 bg-slate-50 rounded-lg">
                  <h1 style={{ fontFamily: design?.heading_font, fontSize: '48px', fontWeight: 'bold' }}>
                    Heading 1
                  </h1>
                  <h2 style={{ fontFamily: design?.heading_font, fontSize: '36px', fontWeight: 'bold' }}>
                    Heading 2
                  </h2>
                  <h3 style={{ fontFamily: design?.heading_font, fontSize: '24px', fontWeight: '600' }}>
                    Heading 3
                  </h3>
                  <p style={{ fontFamily: design?.body_font, fontSize: '16px' }}>
                    Body text: The quick brown fox jumps over the lazy dog. This is how your main content will appear across your marketing materials.
                  </p>
                  <p style={{ fontFamily: design?.body_font, fontSize: '14px', color: '#6B7280' }}>
                    Small text: Secondary information and captions use this smaller size.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logos Tab */}
        <TabsContent value="logos" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Logos & Brand Assets</CardTitle>
              <CardDescription>Upload and manage your brand logos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Primary Logo URL</Label>
                  <Input
                    value={design?.logo_primary_url || ''}
                    onChange={(e) => updateDesign('logo_primary_url', e.target.value)}
                    placeholder="https://example.com/logo.png"
                  />
                  {design?.logo_primary_url && (
                    <div className="mt-2 p-4 bg-slate-100 rounded-lg">
                      <img src={design.logo_primary_url} alt="Primary Logo" className="max-h-16 object-contain" />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Secondary Logo URL</Label>
                  <Input
                    value={design?.logo_secondary_url || ''}
                    onChange={(e) => updateDesign('logo_secondary_url', e.target.value)}
                    placeholder="https://example.com/logo-alt.png"
                  />
                  {design?.logo_secondary_url && (
                    <div className="mt-2 p-4 bg-slate-100 rounded-lg">
                      <img src={design.logo_secondary_url} alt="Secondary Logo" className="max-h-16 object-contain" />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Icon/Symbol URL</Label>
                  <Input
                    value={design?.logo_icon_url || ''}
                    onChange={(e) => updateDesign('logo_icon_url', e.target.value)}
                    placeholder="https://example.com/icon.png"
                  />
                  {design?.logo_icon_url && (
                    <div className="mt-2 p-4 bg-slate-100 rounded-lg">
                      <img src={design.logo_icon_url} alt="Icon" className="max-h-12 object-contain" />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Favicon URL</Label>
                  <Input
                    value={design?.favicon_url || ''}
                    onChange={(e) => updateDesign('favicon_url', e.target.value)}
                    placeholder="https://example.com/favicon.ico"
                  />
                  {design?.favicon_url && (
                    <div className="mt-2 p-4 bg-slate-100 rounded-lg">
                      <img src={design.favicon_url} alt="Favicon" className="max-h-8 object-contain" />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Layout Tab */}
        <TabsContent value="layout" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Layout Settings</CardTitle>
              <CardDescription>Configure spacing, borders, and visual effects</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Border Radius</Label>
                  <Select 
                    value={design?.border_radius || '8px'} 
                    onValueChange={(v) => updateDesign('border_radius', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0px">None (0px)</SelectItem>
                      <SelectItem value="4px">Small (4px)</SelectItem>
                      <SelectItem value="8px">Medium (8px)</SelectItem>
                      <SelectItem value="12px">Large (12px)</SelectItem>
                      <SelectItem value="16px">Extra Large (16px)</SelectItem>
                      <SelectItem value="9999px">Pill</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Shadow Style</Label>
                  <Select 
                    value={design?.shadow_style || 'medium'} 
                    onValueChange={(v) => updateDesign('shadow_style', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SHADOW_STYLES.map(style => (
                        <SelectItem key={style.value} value={style.value}>{style.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Base Spacing (px)</Label>
                  <Input
                    type="number"
                    value={design?.spacing_unit || 8}
                    onChange={(e) => updateDesign('spacing_unit', parseInt(e.target.value) || 8)}
                    min={4}
                    max={16}
                  />
                </div>
              </div>

              {/* Layout Preview */}
              <div className="border-t pt-6">
                <Label className="text-base mb-4 block">Layout Preview</Label>
                <div className="flex gap-4">
                  {['none', 'subtle', 'medium', 'strong'].map((shadow, idx) => (
                    <div
                      key={shadow}
                      className={`p-4 bg-white ${
                        shadow === 'none' ? '' :
                        shadow === 'subtle' ? 'shadow-sm' :
                        shadow === 'medium' ? 'shadow-md' :
                        'shadow-lg'
                      }`}
                      style={{ 
                        borderRadius: design?.border_radius || '8px',
                        border: shadow === 'none' ? '1px solid #e5e7eb' : 'none'
                      }}
                    >
                      <p className="text-sm font-medium capitalize">{shadow}</p>
                      <p className="text-xs text-muted-foreground">Shadow</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DesignManagerPage;
